// frontend/src/infrastructure/repositories/SyncQueueRepository.ts
//
// Repozytorium kolejki synchronizacji offline.
// Jedyne miejsce w kodzie, które zna szczegóły Dexie/IndexedDB dla `syncQueue`.
// Warstwa aplikacji zależy wyłącznie od interfejsu `ISyncQueueRepository`
// (Dependency Inversion — Clean Architecture).

import type { AppDatabase, NewSyncQueueItem, SyncQueueItem } from '../offline/AppDatabase'

/**
 * Kontrakt repozytorium kolejki synchronizacji.
 * Pozwala kolejkować operacje wykonane offline bez wiązania się
 * z konkretną technologią składowania.
 */
export interface ISyncQueueRepository {
  /**
   * Dodaje operację na koniec kolejki.
   * @param entityType typ encji, np. `'incident'`.
   * @param action rodzaj operacji, np. `'create'`.
   * @param payload serializowalny ładunek operacji.
   * @returns identyfikator (klucz główny) utworzonego wpisu.
   */
  enqueue(entityType: string, action: string, payload: unknown): Promise<number>

  /**
   * Zwraca wszystkie oczekujące operacje posortowane rosnąco
   * po `createdAt` (kolejność FIFO — najstarsze najpierw).
   */
  getPending(): Promise<SyncQueueItem[]>

  /**
   * Usuwa z kolejki przetworzoną operację.
   * Idempotentne — usunięcie nieistniejącego `id` nie jest błędem.
   */
  dequeue(id: number): Promise<void>

  /**
   * Zwraca wpisy kolejki dla wskazanego typu encji (np. `'note'`) — niezależnie
   * od tego, czy operacja została już zsynchronizowana z serwerem. Używane przez
   * wyszukiwanie brzegowe offline (Edge Search Fallback,
   * `services/offlineSearchResults.ts`): notatka zapisana lokalnie musi być
   * przeszukiwalna, zanim jeszcze dotrze do backendu.
   */
  getByEntityType(entityType: string): Promise<SyncQueueItem[]>
}

/**
 * Implementacja `ISyncQueueRepository` oparta o Dexie.
 * Instancję bazy wstrzykujemy przez konstruktor — dzięki temu w testach
 * podstawiamy bazę na `fake-indexeddb`, a w aplikacji współdzielony singleton.
 */
export class SyncQueueRepository implements ISyncQueueRepository {
  constructor(private readonly database: AppDatabase) {}

  async enqueue(entityType: string, action: string, payload: unknown): Promise<number> {
    const item: NewSyncQueueItem = {
      entityType,
      action,
      payload,
      createdAt: Date.now(),
    }

    // Dexie zwraca wygenerowany klucz główny (`++id`).
    return this.database.syncQueue.add(item)
  }

  async getPending(): Promise<SyncQueueItem[]> {
    return this.database.syncQueue.orderBy('createdAt').toArray()
  }

  async dequeue(id: number): Promise<void> {
    await this.database.syncQueue.delete(id)
  }

  async getByEntityType(entityType: string): Promise<SyncQueueItem[]> {
    return this.database.syncQueue.where('entityType').equals(entityType).toArray()
  }
}
